import { DataJSON } from '../definitions/BackflippDefinitions';
import { slugify } from './slugify';
import { Flyer } from '../definitions/FlyerDefinitions';
import { Coupon } from '../definitions/CouponDefinitions';
import { getCouponsLink } from './links';
import { getDataByPostalCode } from './common';
import { LOCATION_FOR_BOT } from '../hooks/useLocationDetect';

export interface FlyerItemCoupon {
  id: number;
  flyerId: number;
  flyer: Flyer;
  flyerItemId: number;
  merchant: string;
  merchantLogo: string;
  couponId: number;
  left: number;
  bottom: number;
  right: number;
  top: number;
  item: {
    id: number;
    currentPrice: number;
    originalPrice: number;
    dollarsOff: number;
    cutoutUrl: string;
  };
  couponDollarsOff: number;
  totalSavings: number;
  finalPrice: number;
  percentOff: number;
  displayFinalPrice: boolean;
  displayType: number;
  itemRank: number;
}

export interface CouponDetails {
  id: number;
  disclaimer_text: string;
}

export interface TermsJSON {
  refreshed_at: string;
  coupons: CouponDetails[];
}

// const cacheKey = (id: string) => `coupon-${id}`;

export async function getCouponDetails(id: string, locale: string): Promise<CouponDetails> {
  const postalCode = LOCATION_FOR_BOT[locale].postalCode;
  const data = await getDataByPostalCode(postalCode, locale);
  const coupon = data.coupons.find((c) => Number(c.coupon_id) === Number(id));
  return { id: coupon.coupon_id, disclaimer_text: coupon.disclaimer_text };
}

export function getCoupons(dataJSON: DataJSON, redemptionMethod?: string) {
  // lift flyers into map by flyer id, so we can get the flyer merchant
  // name per matchup
  // FIXME another backflipp thing.
  const flyersById: any = {};
  dataJSON.flyers.forEach((flyer) => (flyersById[flyer.id] = flyer));

  return dataJSON.coupons
    .filter((coupon) => (redemptionMethod ? coupon.redemption_method === redemptionMethod : true))
    .map((coupon) => {
      const matchUps = dataJSON.flyer_item_coupons
        .filter((item) => item.coupon_id === coupon.coupon_id)

        // NOTE: This also excludes matchups with invalid flyer ids
        //       this has happened, and should not be removed.
        .filter((matchup) => {
          const flyer = flyersById[matchup.flyer_id];
          return flyer;
        })

        // FIXME data munge
        .map((item) => {
          const flyer = flyersById[item.flyer_id];
          if (flyer) {
            item.merchant = flyer.merchant;
            item.merchant_logo = flyer.merchant_logo;
          }
          return item;
        });

      matchUps.sort((a, b) => {
        return a.item_rank !== b.item_rank
          ? a.item_rank - b.item_rank // item_rank ASC
          : b.id - a.id; // id DESC
      });

      return liftCoupon(dataJSON, coupon, matchUps);
    });
}

export function liftCoupon(json: DataJSON, coupon_raw: any, matchups_raw: any[] = []): Coupon {
  const coupon = new Coupon(coupon_raw);
  coupon.matchUps = matchups_raw.map((matchup) => liftFlyerItemCoupon(json, matchup));
  return coupon;
}

function liftFlyerItemCoupon(json: DataJSON, raw: any): FlyerItemCoupon {
  return {
    id: raw.id,
    flyerId: raw.flyer_id,
    flyer: new Flyer(json.flyers.find((flyer) => flyer.id === raw.flyer_id)),
    flyerItemId: raw.flyer_item_id,
    merchant: raw.merchant,
    merchantLogo: raw.merchant_logo,
    couponId: raw.coupon_id,
    left: raw.left,
    bottom: raw.bottom,
    right: raw.right,
    top: raw.top,
    item: {
      id: raw.flyer_item_id,
      currentPrice: raw.item_current_price,
      originalPrice: raw.item_original_price,
      dollarsOff: raw.item_dollars_off,
      cutoutUrl: raw.item_cutout_url,
    },
    couponDollarsOff: raw.coupon_dollars_off,
    totalSavings: raw.total_savings,
    finalPrice: raw.final_price,
    percentOff: raw.percent_off,
    displayFinalPrice: raw.display_final_price,
    displayType: raw.display_type,
    itemRank: raw.item_rank,
  };
}

export function isHeroCoupon(coupon: Coupon) {
  const matchup = coupon.matchUps.length > 0 ? coupon.matchUps[0] : null;
  return (
    matchup &&
    coupon.dollarsOff !== null &&
    matchup.finalPrice !== null &&
    matchup.percentOff !== null &&
    matchup.displayFinalPrice === true
  );
}

export function compareCoupon(a: Coupon, b: Coupon) {
  if (a.matchUps.length > 0 && b.matchUps.length === 0) return -1;
  if (b.matchUps.length > 0 && a.matchUps.length === 0) return 1;

  if (a.priority !== b.priority) {
    return a.priority - b.priority;
  }

  //
  if (a.matchUps.length > 0 && b.matchUps.length > 0) {
    const aMatch = a.matchUps[0],
      bMatch = b.matchUps[0];
    if (aMatch.percentOff !== null && bMatch.percentOff === null) return -1;
    if (bMatch.percentOff !== null && aMatch.percentOff === null) return 1;
    if (aMatch.percentOff !== bMatch.percentOff) {
      return bMatch.percentOff - aMatch.percentOff;
    }
  }

  const aFrom = a.availableFrom.getTime();
  const bFrom = b.availableFrom.getTime();
  if (aFrom !== bFrom) {
    return bFrom - aFrom;
  }

  return b.id - a.id;
}

export function getCouponName(coupon: Coupon): string {
  return coupon.saleStory || coupon.promotionText || '';
}

export function getExpired(coupon: Coupon): boolean {
  return coupon.availableTo ? coupon.availableTo < new Date() : false;
}

export function getCouponLink(coupon: Coupon, cityState: string, locale: string): string {
  const now = new Date();
  if (coupon.availableTo < now || coupon.validTo < now) {
    return getCouponsLink(locale, cityState);
  }
  const slug = coupon.id + (coupon.brand ? `-${slugify(coupon.brand)}` : '');
  return `/${locale}/${cityState}/coupon/${slug}`;
}

export function getCouponById(id: number, coupons: Coupon[]): Coupon {
  return coupons.find((c) => {
    return c.id === id;
  });
}
